// The HTML entry's head on Peoply Kariyer's own domain (JOBS_SITE_URL). The entry (client/index.html)
// carries Peoply's own SEO tags — the HR platform's title and description, peoply.ai canonical,
// hreflang, Open Graph, Twitter and JSON-LD — which must not show on the listings' domain. Pure
// string work, shared by the server (production) and the client's dev server.

export interface JobsSiteHead {
  title: string;
  description: string;
  canonicalUrl: string;
  siteName: string;
  imageUrl: string;
  robots: string;
}

const escapeAttribute = (value: string) =>
  value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/**
 * The entry without Peoply's SEO tags (description, keywords, robots, canonical, hreflang, Open
 * Graph, Twitter, JSON-LD); its <title> stays for the caller to replace.
 */
export const stripEntrySeo = (html: string): string =>
  html
    .replace(/<meta\s[^>]*?(?:name="(?:description|keywords|robots|twitter:[^"]*)"|property="og:[^"]*")[^>]*>\s*/g, '')
    .replace(/<link\s[^>]*?rel="(?:canonical|alternate)"[^>]*>\s*/g, '')
    .replace(/<script\s+type="application\/ld\+json"[^>]*>[\s\S]*?<\/script>\s*/g, '');

/** The SEO tags of a page on the listings' domain. */
export const jobsSiteHeadTags = (head: JobsSiteHead): string =>
  [
    `<meta name="description" content="${escapeAttribute(head.description)}">`,
    `<meta name="robots" content="${escapeAttribute(head.robots)}">`,
    `<link rel="canonical" href="${escapeAttribute(head.canonicalUrl)}">`,
    `<meta property="og:type" content="website">`,
    `<meta property="og:site_name" content="${escapeAttribute(head.siteName)}">`,
    `<meta property="og:title" content="${escapeAttribute(head.title)}">`,
    `<meta property="og:description" content="${escapeAttribute(head.description)}">`,
    `<meta property="og:url" content="${escapeAttribute(head.canonicalUrl)}">`,
    `<meta property="og:image" content="${escapeAttribute(head.imageUrl)}">`,
    `<meta name="twitter:card" content="summary_large_image">`,
    `<meta name="twitter:title" content="${escapeAttribute(head.title)}">`,
    `<meta name="twitter:description" content="${escapeAttribute(head.description)}">`,
    `<meta name="twitter:image" content="${escapeAttribute(head.imageUrl)}">`,
  ].join('\n');

// The listings' domain's name and description, for the pages the candidate app draws itself (About,
// legal texts, sign-in / sign-up, the candidate area); the app then sets a page's own title.
export const JOBS_SITE_NAME = 'Peoply Kariyer';
export const JOBS_SITE_DESCRIPTION =
  "Peoply Kariyer'de sana uygun iş ilanlarını keşfet, tek profille başvur ve başvurunu adım adım takip et.";
// The link preview image (client/public), from the listings' domain itself.
export const JOBS_SITE_IMAGE_PATH = '/opengraph-image.png';

// The listings' domain's own public pages besides the listings (single-segment, yet not a listing).
export const JOBS_SITE_PUBLIC_PAGE_PATHS: readonly string[] = ['/about', '/privacy', '/terms-and-conditions'];

// A candidate's own pages on the listings' domain: sign-in / sign-up, the end of an OAuth sign-in and
// the password reset (plus the candidate area, /careers*). Not for search engines.
const JOBS_SITE_CANDIDATE_PAGE_PATHS: readonly string[] = ['/auth/login', '/auth/signup', '/auth/oauth', '/reset-password'];

/**
 * Whether the candidate app draws a page at `pathname` on the listings' domain (besides the
 * listings, a listing and a company page). Peoply's own pages (the HR app, pricing, module pages…)
 * have no address there: anything else is a not-found page.
 */
export const isJobsSiteAppPage = (pathname: string): boolean =>
  pathname === '/careers' ||
  pathname.startsWith('/careers/') ||
  JOBS_SITE_PUBLIC_PAGE_PATHS.includes(pathname) ||
  JOBS_SITE_CANDIDATE_PAGE_PATHS.includes(pathname);

/**
 * The head of such a page at `pathname` on the listings' domain (`siteUrl`, no trailing slash). Only
 * the public pages are for search engines; a candidate's own pages and a not-found page are not.
 */
export const jobsSiteAppHead = (siteUrl: string, pathname: string): JobsSiteHead => ({
  title: JOBS_SITE_NAME,
  description: JOBS_SITE_DESCRIPTION,
  canonicalUrl: `${siteUrl}${pathname}`,
  siteName: JOBS_SITE_NAME,
  imageUrl: `${siteUrl}${JOBS_SITE_IMAGE_PATH}`,
  robots: JOBS_SITE_PUBLIC_PAGE_PATHS.includes(pathname) ? 'index, follow' : 'noindex, follow',
});

/** The entry with the listings' domain's own title and SEO tags in place of Peoply's. */
export const withJobsSiteHead = (html: string, head: JobsSiteHead): string =>
  stripEntrySeo(html)
    .replace(/<title>[\s\S]*?<\/title>/, () => `<title>${escapeAttribute(head.title)}</title>`)
    .replace('</head>', () => `${jobsSiteHeadTags(head)}\n</head>`);
